import { useEffect, useMemo, useRef, useState } from 'react';

import { useFormikContext } from 'formik';
import dayjs from 'dayjs';

import { Select } from 'components/UIComponents';
import { FieldGroupTitleContainer } from 'components/UIComponents/layout/styledFormComponents';

import { IPodcastForm, IOriginSpot } from 'interfaces/spots/Podcast/IPodcastForm';

import OriginSpotsField from './OriginSpotsField';
import OriginSpotPicker from './OriginSpotPicker';

const BONUS_TYPE_OPTIONS = [
  { key: 'AV (Added Value – Part of Buy)',          value: 'AV' },
  { key: 'AVP (Added Value – Support Performance)', value: 'AVP' },
  { key: 'MGE (Make Good – Error)',                 value: 'MGE' },
  { key: 'MGU (Make Good – Underdelivery)',         value: 'MGU' },
];

interface BonusTypeSectionProps {
  disabled: boolean;
  currentSpotId?: number;
}

const isMgType = (v: IPodcastForm['bonusType']) => v === 'MGE' || v === 'MGU';

const BonusTypeSection = ({ disabled, currentSpotId }: BonusTypeSectionProps) => {
  const { values, errors, touched, setFieldValue, setFieldTouched } =
    useFormikContext<IPodcastForm>();
  const [pickerOpen, setPickerOpen] = useState(false);

  const isMG = isMgType(values.bonusType);
  const hasParents = !!values.outletId && !!values.productId;
  const pickerEnabled = !disabled && isMG && hasParents;

  const prevRef = useRef({
    bonusType: values.bonusType,
    outletId:  values.outletId,
    productId: values.productId,
  });

  useEffect(() => {
    const prev = prevRef.current;
    const typeChanged    = !!prev.bonusType && prev.bonusType !== values.bonusType;
    const parentChanged  = (!!prev.outletId && prev.outletId !== values.outletId) ||
                           (!!prev.productId && prev.productId !== values.productId);
    const hasSelection   = (values.originSpotIds?.length ?? 0) > 0;
    const leftMg         = !isMgType(values.bonusType);

    if (hasSelection && (parentChanged || (typeChanged && leftMg))) {
      setFieldValue('originSpotIds', []);
      setFieldValue('originSpots', []);
    }

    prevRef.current = {
      bonusType: values.bonusType,
      outletId:  values.outletId,
      productId: values.productId,
    };
  }, [values.bonusType, values.outletId, values.productId, values.originSpotIds, setFieldValue]);

  const collapsedLabel = useMemo(() => {
    const spots = values.originSpots ?? [];
    if (spots.length === 0) return '';
    const byOutlet = new Map<string, string[]>();
    spots.forEach((s) => {
      const arr = byOutlet.get(s.outletName) ?? [];
      arr.push(dayjs(s.startDate).format('MM/DD/YYYY'));
      byOutlet.set(s.outletName, arr);
    });
    return Array.from(byOutlet.entries())
      .map(([outlet, dates]) => `${outlet} (${dates.join(', ')})`)
      .join(', ');
  }, [values.originSpots]);

  const originError =
    typeof errors.originSpotIds === 'string' ? errors.originSpotIds : undefined;
  const showOriginError = !!(touched.originSpotIds && originError);

  return (
    <>
      <FieldGroupTitleContainer>BONUS TYPE</FieldGroupTitleContainer>
      <Select
        name="bonusType"
        label="Type"
        options={BONUS_TYPE_OPTIONS}
        disabled={disabled}
      />
      <OriginSpotsField
        label="Origin spot(s)"
        value={collapsedLabel}
        disabled={!pickerEnabled}
        onClick={() => {
          setFieldTouched('originSpotIds', true, false);
          setPickerOpen(true);
        }}
        helperText={showOriginError ? originError : undefined}
        error={showOriginError}
      />
      {pickerOpen && (
        <OriginSpotPicker
          outletId={values.outletId as number}
          productId={values.productId as number}
          excludeId={currentSpotId}
          initiallySelected={values.originSpotIds ?? []}
          initiallySelectedSpots={values.originSpots ?? []}
          onCancel={() => setPickerOpen(false)}
          onSave={(selected: IOriginSpot[]) => {
            setFieldValue('originSpotIds', selected.map((s) => s.id));
            setFieldValue('originSpots', selected);
            setPickerOpen(false);
          }}
        />
      )}
    </>
  );
};

export default BonusTypeSection;
